import { useCallback } from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { useSelector, useDispatch } from 'react-redux';

import { appSettings } from 'wanderu-config';
import { googleDataSelectors } from 'wanderu-ducks-google';
import {
    filterActions,
    filterSelectors,
    searchSelectors,
    tripSelectors,
} from 'wanderu-ducks-search';

import type { StationMapCoreProps } from './StationMapCore';
import type { TrackEvent } from './events';

type StationMapData = Omit<StationMapCoreProps, 'mapContainerStyle' | 'onClose' | 'showCityMarker'> & {
    filteredTripCount: number;
};

export default function useStationMapData(direction: string): StationMapData | null {
    const dispatch = useDispatch<any>();

    const searchParams = useSelector((state: any) => searchSelectors.selectReduxSearchParams(state, direction),);
    const { originPlaceId, destinationPlaceId } = searchParams;

    const departGeo = useSelector((state: any) => (googleDataSelectors.selectLatLongData as any)(state, originPlaceId),);
    const arriveGeo = useSelector((state: any) => (googleDataSelectors.selectLatLongData as any)(state, destinationPlaceId),);

    const trips = useSelector((state: any) => (tripSelectors.selectFilteredAndSortedTrips as any)(state, searchParams),);
    const isArrivalStationsFiltered = useSelector((state: any) => filterSelectors.selectArriveStationsIsFiltered(state),);
    const isDepartureStationsFiltered = useSelector((state: any) => filterSelectors.selectDepartStationsIsFiltered(state),);
    const totalTripsUnfiltered = useSelector((state: any) => (tripSelectors.selectSearchResults(state) || []).length,);
    const stationData = useSelector((state: any) => (tripSelectors.selectStationsTripData as any)(state, searchParams),);
    const departStationCount = stationData
        ? Object.keys(stationData.departure || {}).length
        : 0;
    const arriveStationCount = stationData
        ? Object.keys(stationData.arrival || {}).length
        : 0;

    const resetArrivalStations = useCallback(
        () => dispatch(filterActions.resetArriveStations()),
        [dispatch],
    );
    const resetDepartureStations = useCallback(
        () => dispatch(filterActions.resetDepartStations()),
        [dispatch],
    );
    const setArrivalStationOnlyActive = useCallback(
        (id: string) => dispatch(filterActions.setArriveStationOnlyActive(id)),
        [dispatch],
    );
    const setDepartureStationOnlyActive = useCallback(
        (id: string) => dispatch(filterActions.setDepartStationOnlyActive(id)),
        [dispatch],
    );

    const trackEvent: TrackEvent = useCallback(
        (name, properties) => amplitude.track(name, properties),
        [],
    );

    if (!departGeo?.latitude || !arriveGeo?.latitude) {
        return null;
    }

    return {
        arriveLocation: { lat: arriveGeo.latitude, lng: arriveGeo.longitude },
        departLocation: { lat: departGeo.latitude, lng: departGeo.longitude },
        filteredTripCount: trips.length,
        googleMapsApiKey: appSettings.config.google.mapsApiKey,
        isArrivalStationsFiltered,
        isDepartureStationsFiltered,
        resetArrivalStations,
        resetDepartureStations,
        searchParams,
        setArrivalStationOnlyActive,
        setDepartureStationOnlyActive,
        arriveStationCount,
        departStationCount,
        totalTripsUnfiltered,
        trackEvent,
        trips,
    };
}
